import { Star, Quote, ExternalLink } from "lucide-react";
import { FRESHA_BOOKING_URL as FRESHA_URL, GOOGLE_REVIEW_URL } from "../config";

export const ReviewsSection = ({ reviews }) => {
  const hasReviews = reviews && reviews.length > 0;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-[#C5A059] text-sm tracking-[0.3em] uppercase mb-4"
            data-testid="reviews-tagline"
          >
            Kundenstimmen
          </p>
          <h2
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-6"
            data-testid="reviews-title"
          >
            Was unsere <span className="italic">Kunden</span> sagen
          </h2>
          <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] mx-auto" />
        </div>

        {hasReviews ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div
                key={review.id}
                className={`testimonial-card p-8 md:p-10 relative ${
                  index === 0 ? "lg:col-span-2" : ""
                }`}
                data-testid={`review-card-${review.id}`}
              >
                <Quote size={32} className="text-[#C5A059]/30 mb-4" aria-hidden="true" />
                <div className="flex gap-1 mb-4" data-testid={`review-rating-${review.id}`}>
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#C5A059] text-[#C5A059]" />
                  ))}
                </div>
                <p
                  className="font-cormorant text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-6 not-italic"
                  data-testid={`review-comment-${review.id}`}
                >
                  "{review.comment}"
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full bg-[#D4C4B7] flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <span className="font-playfair text-lg text-[#1A1A1A]">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p
                      className="font-medium text-[#1A1A1A] not-italic"
                      data-testid={`review-author-${review.id}`}
                    >
                      {review.name}
                    </p>
                    <p className="text-sm text-[#666666] not-italic">
                      {new Date(review.date).toLocaleDateString("de-CH", {
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state – points to real reviews on Fresha */
          <div
            className="max-w-2xl mx-auto text-center bg-white border border-[#E5E5E5] p-10 md:p-16"
            data-testid="reviews-empty"
          >
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={22} className="fill-[#C5A059] text-[#C5A059]" />
              ))}
            </div>
            <p className="font-cormorant italic text-2xl md:text-3xl text-[#1A1A1A] mb-4">
              Ihre Meinung ist uns wichtig
            </p>
            <p className="text-[#666666] leading-relaxed mb-8">
              Lesen Sie echte Erfahrungen unserer Kundinnen und hinterlassen Sie
              Ihre eigene Bewertung direkt auf unserem Fresha-Profil.
            </p>
            <a
              href={FRESHA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="reviews-fresha-btn"
            >
              <ExternalLink size={16} />
              Bewertungen auf Fresha
            </a>
          </div>
        )}

        {/* Google review CTA */}
        {hasReviews && (
          <div className="text-center mt-16" data-testid="reviews-google-cta">
            <p className="font-cormorant italic text-xl md:text-2xl text-[#1A1A1A] mb-6">
              Waren Sie bei uns zu Gast? Wir freuen uns über Ihre Bewertung.
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-8 py-4 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#C5A059] hover:text-[#1A1A1A] transition-colors duration-300"
              data-testid="reviews-google-btn"
            >
              <Star size={16} className="fill-current" />
              Auf Google bewerten
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
